/**
 * Contract progress from space events (plan §2.6, §6 M2-08). The career runtime calls these while draining the
 * space event ring: `groupDefeated` with kind contract → `onGroupDefeated`; `partSecured` (its `securedLog`
 * entry, after the part went into the hold) → `onPartSecured`. A job that reaches its goal turns `ready` and
 * can be handed in (`commands.handInContract`). Deliveries and couriers need no event: hand-in checks the hold.
 * In the Reach (M3 R22g) `laneRingRepaired`, `partsDelivered` and `systemCharted` drive the three Reach types.
 *
 * Pure: the same career comes back when nothing changes. Career layer, not per-frame.
 */
import type { ComponentType } from '../data/m2Schemas'
import type { Career, ContractState } from '../career/types'

/** What `onPartSecured` needs from a secured part (a `CargoComponent` or a `securedLog` entry). */
export interface SecuredPartRef {
  type: ComponentType
  contractId?: string | null
}

/** Sets active contract `contractId` to `ready` (the same career when it is missing or already ready). */
function markReady(career: Career, contractId: string, type: ContractState['type']): Career {
  const i = career.contracts.active.findIndex((c) => c.id === contractId && c.type === type)
  if (i < 0 || career.contracts.active[i].status === 'ready') return career
  const active = career.contracts.active.map((c, j) => (j === i ? { ...c, status: 'ready' as const } : c))
  return { ...career, contracts: { ...career.contracts, active } }
}

/** A clear-raiders contract's group was destroyed: the job is ready to hand in. */
export function onGroupDefeated(career: Career, contractId: string): Career {
  return markReady(career, contractId, 'clearRaiders')
}

/**
 * A part was secured: a recovery job turns ready when its component (tagged with the contract id and of the
 * named type) is in the hold.
 */
export function onPartSecured(career: Career, part: SecuredPartRef): Career {
  const id = part.contractId
  if (id === undefined || id === null) return career
  const contract = career.contracts.active.find((c) => c.id === id && c.type === 'recovery')
  if (contract === undefined || part.type !== contract.params.componentType) return career
  const inHold = career.cargo.components.some((c) => c.contractId === id && c.type === contract.params.componentType)
  return inHold ? markReady(career, id, 'recovery') : career
}

// ---------------------------------------------------------------------------------------------
// M3 bridge (plan §7 item 4, R22g): Reach events drive the three Reach types. The bridge
// (`src/bridge/m2/contracts.ts`) calls these when it reads the Reach event ring.
// ---------------------------------------------------------------------------------------------

/** True when an active (not yet ready) job of `type` matches `match`. Cheap guard before a career write. */
export function hasOpenJob(career: Career, type: ContractState['type'], match: (params: ContractState['params']) => boolean): boolean {
  return career.contracts.active.some((c) => c.type === type && c.status === 'active' && match(c.params))
}

/** Marks every active job of `type` whose params match ready (the same career when none does). */
function markMatching(career: Career, type: ContractState['type'], match: (params: ContractState['params']) => boolean): Career {
  if (!hasOpenJob(career, type, match)) return career
  const active = career.contracts.active.map((c) => (c.type === type && c.status === 'active' && match(c.params) ? { ...c, status: 'ready' as const } : c))
  return { ...career, contracts: { ...career.contracts, active } }
}

/** `REV.laneRingRepaired`: a lane-repair job on ring `ringId` is ready. */
export function onLaneRingRepaired(career: Career, ringId: string): Career {
  return markMatching(career, 'laneRepair', (p) => p.ringId === ringId)
}

/** `REV.systemCharted`: a chart-a-system job for `systemId` is ready. */
export function onSystemCharted(career: Career, systemId: string): Career {
  return markMatching(career, 'chartSystem', (p) => p.systemId === systemId)
}

/**
 * `REV.partsDelivered`: `qty` parts (any mix) went into relay `relayId`'s port. Every open relay-parts job for that
 * relay counts them; one turns ready once `delivered` reaches its `units`.
 */
export function onPartsDelivered(career: Career, relayId: string, qty: number): Career {
  const n = Math.floor(qty)
  if (!(n > 0) || !hasOpenJob(career, 'relayParts', (p) => p.relayId === relayId)) return career
  const active = career.contracts.active.map((c) => {
    if (c.type !== 'relayParts' || c.status !== 'active' || c.params.relayId !== relayId) return c
    const delivered = (c.params.delivered ?? 0) + n
    const done = delivered >= (c.params.units ?? 0)
    return { ...c, params: { ...c.params, delivered }, status: done ? ('ready' as const) : c.status }
  })
  return { ...career, contracts: { ...career.contracts, active } }
}
